import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight } from 'lucide-react';
import type { AIInvoiceContext } from '../../types';

interface ViewPointChipProps {
  context: 'validation' | 'duplicate' | 'overdue' | 'missing_fields';
  /** The chip's label. */
  promptText: string;
  /** The question put in the chat box; defaults to the label. */
  prefillMessage?: string;
  contextData?: any;
  invoiceContext?: AIInvoiceContext;
  className?: string;
}

export const ViewPointChip: React.FC<ViewPointChipProps> = ({
  context,
  promptText,
  prefillMessage,
  contextData,
  invoiceContext,
  className = ''
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    try {
      // Navigate to AI page with pre-filled context
      navigate('/ai', {
        state: {
          prefillMessage: prefillMessage ?? promptText,
          contextData: contextData,
          source: `dashboard_${context}`,
          invoiceContext: invoiceContext
        }
      });
    } catch (error) {
      console.error('Navigation error:', error);
      // Fallback: navigate without state
      navigate('/ai');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <button
      onClick={handleClick}
      onKeyPress={handleKeyPress}
      className={`
        inline-flex items-center gap-2 mt-2 px-3 py-1.5
        rounded-md text-xs font-medium
        bg-transparent
        text-purple-600 dark:text-purple-300
        border border-purple-500/25 dark:border-purple-500/20
        hover:bg-purple-500/10 hover:border-purple-500/40
        hover:text-purple-700 dark:hover:text-purple-200
        active:scale-95
        transition-all duration-200 ease-out
        focus:outline-none focus:ring-2 focus:ring-purple-500/30
        w-full
        group
        ${className}
      `}
      aria-label={`Ask ViewPoint: ${promptText}`}
      role="button"
      tabIndex={0}
    >
      <Sparkles className="w-3.5 h-3.5 shrink-0 opacity-70 group-hover:opacity-100 transition-opacity" />
      <span className="flex-1 text-left">{promptText}</span>
      <ArrowRight className="w-3.5 h-3.5 shrink-0 opacity-50 group-hover:opacity-100 transition-all duration-200 group-hover:translate-x-0.5" />
    </button>
  );
};

export default ViewPointChip;
